import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  Input,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchExpenseClaimDetails } from '../../../store/apis/expenseService';
import { useExpenseClaimUtilCtx } from '../../../pages/ExpenseClaims/context/ExpenseClaimUtilProvider';
import { DateUtil } from '../../../utils';
import { CLAIM_FORM_TYPES } from '../../../pages/ExpenseClaims/Employee/EmployeeExpenseClaim/constants';

import './AdvanceRecoveryModal.scss';

function AdvanceRecoveryModal({
  isOpen,
  toggle,
  claimNo,
  formType = 'create',
  employeeId,
  ...props
}) {
  const queryClient = useQueryClient();
  const { saveARExpenseClaimMutation } = useExpenseClaimUtilCtx();
  const [advanceRecoveryFormList, setAdvanceRecoveryFormList] = useState([]);

  const { data: advanceRecoveryList, isLoading: advanceRecoveryLoading } =
    useQuery({
      queryKey: ['expense-claim-advance-recovery', claimNo],
      queryFn: () =>
        fetchExpenseClaimDetails({
          claimNo: claimNo || '',
          listType: 'LOAN',
          employeeId,
        }),
      enabled: !!claimNo,
    });

  useEffect(() => {
    if (advanceRecoveryList && advanceRecoveryList.length > 0) {
      setAdvanceRecoveryFormList(advanceRecoveryList);
    }
  }, [advanceRecoveryList]);

  const renderAttachments = () => {
    if (advanceRecoveryLoading) {
      return (
        <tr>
          <td colSpan="5" className="text-center py-2">
            <span>Loading attachments...</span>
          </td>
        </tr>
      );
    }
    if (!advanceRecoveryFormList || advanceRecoveryFormList.length === 0) {
      return (
        <tr>
          <td colSpan="3" className="text-center py-2">
            No advance recovery records available.
          </td>
        </tr>
      );
    }
    return advanceRecoveryFormList?.map((item, index) => (
      <tr key={item.id}>
        <td className="py-2">{index + 1}</td>
        <td className="py-2">{item?.['Loan Type']}</td>
        <td className="py-2">{item?.TransNo}</td>
        <td className="py-2">
          {DateUtil.isoToDdMmYyyySlice(item?.PaymentDate)}
        </td>
        <td className="py-2">{item?.Curr.trim()}</td>
        <td className="py-2">{item?.Amt}</td>
        <td className="py-2">{item?.Balance}</td>
        <td className="py-2 d-flex">
          <Input
            type="number"
            className="form-control external-filter-input"
            value={item?.['Claim Recovery']}
            onChange={e => {
              const updatedList = advanceRecoveryFormList.map(formItem => {
                if (formItem.id === item.id) {
                  return { ...formItem, 'Claim Recovery': +e.target.value };
                }
                return formItem;
              });
              setAdvanceRecoveryFormList(updatedList);
            }}
            disabled={formType === CLAIM_FORM_TYPES.VIEW}
          />
        </td>
      </tr>
    ));
  };

  const mapARSaveData = () =>
    advanceRecoveryFormList.map((item, index) => ({
      claimNo,
      transNo: item.TransNo,
      expAmount: item?.['Claim Recovery'] || 0,
      expLno: index + 1,
    }));

  const computeTotal = () =>
    advanceRecoveryFormList.reduce(
      (total, item) => total + (item?.['Claim Recovery'] || 0),
      0,
    );

  const runValidations = () => {
    const invalidItems = advanceRecoveryFormList.filter(item => {
      const recoveryAmount = item?.['Claim Recovery'] || 0;
      const balance = item?.Balance || 0;
      return recoveryAmount > balance;
    });
    if (invalidItems.length > 0) {
      const errorMessage = invalidItems
        .map(item => `Recovery amount for ${item?.TransNo} exceeds balance.`)
        .join(' ');
      throw new Error(errorMessage);
    }
  };

  const handleARSave = async () => {
    try {
      runValidations();
      const saveData = mapARSaveData();
      await saveARExpenseClaimMutation.mutateAsync(saveData);
      setAdvanceRecoveryFormList([]);
      queryClient.invalidateQueries(['expenseClaimDetailsHeader', claimNo]);
      queryClient.invalidateQueries(['expenseClaimDetails', claimNo]);
      toggle();
    } catch (error) {
      toast.error(error.message || 'Failed to save advance recovery.', {
        className: 'toast-error',
      });
    }
  };

  return (
    <div>
      <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
        <ModalHeader toggle={toggle}>Advance Recovery</ModalHeader>
        <ModalBody className="advance-recovery-modal px-3">
          <div
            className="expense-claim-advance-recovery-table-outer"
            style={{
              border: '2px solid #D0D5DD',
              borderRadius: '8px',
            }}
          >
            <Table className="expense-claim-advance-recovery-table">
              <thead>
                <tr>
                  <th
                    className="py-2 fw-semibold"
                    style={{
                      width: '50px',
                    }}
                  >
                    #
                  </th>
                  <th className="py-2 fw-semibold">Advance Type</th>
                  <th className="py-2 fw-semibold">TransNo</th>
                  <th className="py-2 fw-semibold">Issue Date</th>
                  <th className="py-2 fw-semibold">Curr</th>
                  <th className="py-2 fw-semibold">Amount</th>
                  <th className="py-2 fw-semibold">Balance</th>
                  <th className="py-2 fw-semibold">Recovery</th>
                </tr>
              </thead>
              <tbody
                style={{
                  maxHeight: props?.maxHeight || '130px',
                }}
              >
                {renderAttachments()}
              </tbody>
            </Table>
          </div>
          <div>
            <div className="d-flex justify-content-end align-items-center mt-2 px-3">
              <span className="fw-semibold">Total Amount: </span>
              <span className="ms-2">
                {parseFloat(computeTotal()).toFixed(2)}
              </span>
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-center">
          {formType !== CLAIM_FORM_TYPES.VIEW && (
            <>
              <button
                type="button"
                className="advance-recovery-cancel-button"
                onClick={toggle}
              >
                Close
              </button>
              <button
                type="button"
                className="advance-recovery-upload-button"
                onClick={handleARSave}
              >
                Save
              </button>
            </>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default AdvanceRecoveryModal;
